import { useInView } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiCallStatus, ApiEndpoints } from "../../../api-http";
import { useAuthContext } from "../../../auth";
import { useReCaptchaButton } from "../../../captcha/use-recaptcha-button";
import { useLabels } from "../../../contexts/labels";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { useGetConsents } from "../../../hooks/use-get-consents";
import { useGetGenericApiValues } from "../../../hooks/use-get-generic-api-values";
import { useGetUserInfo } from "../../../hooks/use-get-user-data";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { renameFields } from "../../../utils/object";
import { Container } from "../../container";
import { Spinner } from "../../spinner";
import { SpecialContentHandler } from "../../strips/special-content/types";
import { RequestInfoForm, RequestInfoFormHandle, RequestInfoFormProps } from "./form";
import { mapApiPayloadToFormData, mapFormDataToApiPayload } from "./map-form-data-to-api-payload";
import { ResponseModal } from "./response-modal";
import { RequestInfoFormLabels, RequestInfoFormTypes } from "./types";
import { usePostInfoForm } from "./use-post-info-form";

// TODO
// This const is used for an exception to the usual workflow.
// This is NOT the final version of the component and has been hardcoded
// because it's a last minute requirement.
// We plan to come back and make it proper and clean later, don't worry 💪
const CONSENT_MARKETING_EXCEPTION = "marketing";

export interface RequestInfoFormSectionProps extends Pick<RequestInfoFormProps, "type"> {
  lang: string;
}

export function RequestInfoFormSection({ lang, type }: RequestInfoFormSectionProps) {
  const { labels: stringLabels, richTextLabels } = useLabels();

  const fieldsToRename: Record<keyof RequestInfoFormLabels, string> = {
    firstName: "forms-first-name",
    lastName: "forms-last-name",
    email: "forms-email",
    phone: "forms-phone",
    company: "forms-company",
    materials: "forms-materials",
    country: "forms-country",
    city: "forms-city",
    zipCode: "forms-zip-code",
    state: "forms-state",
    subject: "forms-subject",
    message: "forms-message",
    optionalLabel: "forms-optional-label",
    requiredFieldMessage: "forms-required-field-message",
    wrongPhoneFormat: "forms-wrong-phone-format",
    wrongEmailFormat: "forms-wrong-email-format",
    selectCountryPlaceholder: "forms-select-country-placeholder",
    submit: "forms-submit",
    processOfInterest: "forms-process-of-interest",
    machineSerialNumber: "forms-machine-serial-number",
  };

  const labels = renameFields(
    { ...richTextLabels, ...stringLabels },
    fieldsToRename
  ) as RequestInfoFormLabels;

  const formHandle = useRef<RequestInfoFormHandle>(null);
  const formContainer = useRef<HTMLDivElement>(null);

  // Retrieve all picklists values
  const [countriesList, countryCallStatus] = useGetGenericApiValues(lang, ApiEndpoints.COUNTRY);
  const [statesList, stateCallsStatus] = useGetGenericApiValues(lang, ApiEndpoints.PROVINCES);
  const [materialsList, materialsCallStatus] = useGetGenericApiValues(lang, ApiEndpoints.MATERIALS);
  const [processOfInterest, processOfInterestStatus] = useGetGenericApiValues(
    lang,
    ApiEndpoints.PROCESS_OF_INTEREST
  );
  const [_consents, consentsStatus] = useGetConsents(lang);
  // TODO
  // Exception logic 🤢
  // It overwrite config for a specif case.
  // This will be changed in a future release.
  const consents = useMemo(
    () =>
      type !== SpecialContentHandler.NewsletterForm
        ? _consents
        : _consents?.map((item) =>
            item.consentId !== CONSENT_MARKETING_EXCEPTION ? item : { ...item, mandatory: true }
          ) ?? null,
    [_consents, type]
  );

  const [allFormApiStatus, setAllFormApiStatus] = useState<ApiCallStatus>(ApiCallStatus.LOADING);
  useEffect(() => {
    const statesCallStatus = [
      countryCallStatus,
      stateCallsStatus,
      materialsCallStatus,
      processOfInterestStatus,
      consentsStatus,
    ];
    if (statesCallStatus.every((x) => x === ApiCallStatus.SUCCESS))
      setAllFormApiStatus(ApiCallStatus.SUCCESS);
    else if (statesCallStatus.some((x) => x === ApiCallStatus.FAILED))
      setAllFormApiStatus(ApiCallStatus.FAILED);
    else if (statesCallStatus.some((x) => x === ApiCallStatus.LOADING))
      setAllFormApiStatus(ApiCallStatus.LOADING);
  }, [
    setAllFormApiStatus,
    countryCallStatus,
    materialsCallStatus,
    processOfInterestStatus,
    stateCallsStatus,
    consentsStatus,
  ]);

  // State for form visibility, if false it will show loading spinner
  const [isFormVisible, setIsFormVisible] = useState(false);

  // Recaptcha
  const { handleReCaptchaVerify, isDisabled: isReCaptchaNotReady } = useReCaptchaButton();
  const [recaptchaMissingToken, setRecaptchaMissingToken] = useState(false);

  // Check if logged nad retrieve user values
  const { logged, userInfo } = useAuthContext();
  const [userData, loaded] = useGetUserInfo(userInfo?.sub, lang);
  const [defaultValues, setDefaultValues] = useState<RequestInfoFormTypes>();
  const { pushEvent } = useDataLayer();

  useEffect(() => {
    userData && setDefaultValues(mapApiPayloadToFormData(userData));
    setIsFormVisible(
      allFormApiStatus === ApiCallStatus.SUCCESS && (loaded === ApiCallStatus.SUCCESS || !logged)
    );
  }, [loaded, userData, setDefaultValues, setIsFormVisible, logged, allFormApiStatus]);

  // Submit logic
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { postInfoForm: postApi, isLoading: isPostLoading, error } = usePostInfoForm(type);

  // Send an event the first time the form is viewed (and only first time)
  const isFormContainerVisible = useInView(formContainer);
  const hasSentOpenFormEvent = useRef(false);
  useEffect(() => {
    if (isFormContainerVisible && isFormVisible && !hasSentOpenFormEvent.current) {
      pushEvent({
        event: GoogleAnalyticsEvent.OpenForm,
        form_topic: type,
      });
      hasSentOpenFormEvent.current = true;
    }
  }, [isFormContainerVisible, type, pushEvent, isFormVisible]);

  const onSubmit: RequestInfoFormProps["onSubmit"] = useCallback(
    async (values) => {
      // I should be loaded and in the browser, so window location should be defined
      const currentUrl = window.location.href;
      const token = await handleReCaptchaVerify();
      if (token) {
        postApi(mapFormDataToApiPayload(values, token, currentUrl));
      } else {
        setRecaptchaMissingToken(true);
        console.log("missing recaptcha token");
      }
      setIsSubmitted(true);
    },
    [setIsSubmitted, setRecaptchaMissingToken, handleReCaptchaVerify, postApi]
  );

  // Modal logic
  const [responseStatus, setResponseStatus] = useState<ApiCallStatus>(ApiCallStatus.LOADING);
  const onCloseModal = useCallback(() => {
    setIsSubmitted(false);
    formHandle.current?.resetForm(defaultValues);
  }, [defaultValues, setIsSubmitted]);

  useEffect(() => {
    setResponseStatus(
      error || recaptchaMissingToken
        ? ApiCallStatus.FAILED
        : !isPostLoading
        ? ApiCallStatus.SUCCESS
        : ApiCallStatus.LOADING
    );
  }, [error, recaptchaMissingToken, isPostLoading]);

  return (
    <Container className="py-8 bg-neutral-light" pagePaddingX ref={formContainer}>
      <div className="max-w-4xl mx-auto">
        {allFormApiStatus === ApiCallStatus.FAILED ? (
          <div className="text-lg text-dark font-light mx-auto p-8 text-center">
            {stringLabels["api-network-error"]}
          </div>
        ) : isFormVisible ? (
          <RequestInfoForm
            countriesList={countriesList}
            materialsList={materialsList}
            statesList={statesList}
            processOfInterestList={processOfInterest}
            type={type}
            ref={formHandle}
            defaultValues={defaultValues}
            labels={labels}
            consents={consents}
            onSubmit={onSubmit}
            isRecaptchaNotReady={isReCaptchaNotReady}
          />
        ) : (
          <Spinner className="mx-auto" size="large" />
        )}
        <ResponseModal
          labels={labels}
          type={type}
          onClose={onCloseModal}
          responseStatus={responseStatus}
          isOpen={isSubmitted}
        />
      </div>
    </Container>
  );
}
